import React from "react";
import {Avatar, Box, ButtonBase, Link, Typography} from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {AGENTIC_SECTION_CITATION_MARKER_REGEX} from "./agenticSectionState";

function buildAuthorIdentity(author = {}) {
    return author.uid || author.email || author.displayName || author.headshot || "";
}

function buildAuthorLabel(author = {}) {
    return author.displayName || author.email || author.uid || "Unknown author";
}

function buildAuthorInitials(author = {}) {
    const label = buildAuthorLabel(author);
    return label
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() || "")
        .join("") || "?";
}

function getMarkerAuthors(citations = []) {
    const byIdentity = new Map();

    citations.forEach((citation) => {
        (citation?.authors || []).forEach((author) => {
            const identity = buildAuthorIdentity(author);
            if (!identity || byIdentity.has(identity)) return;
            byIdentity.set(identity, author);
        });
    });

    return Array.from(byIdentity.values());
}

const InlineCitationAvatarGroup = React.memo(function InlineCitationAvatarGroup({citationIds, citations, onClick}) {
    const authors = getMarkerAuthors(citations);
    const authorNames = authors.map(buildAuthorLabel).join(", ");
    const buttonLabel = authors.length > 0
        ? `Open ${citations.length} citations from ${authorNames}`
        : `Open ${citations.length} citations`;
    const handleMouseDown = (event) => {
        event.preventDefault();
    };

    return (
        <ButtonBase
            component="span"
            onClick={onClick}
            onMouseDown={handleMouseDown}
            aria-label={buttonLabel}
            contentEditable={false}
            draggable={false}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                mx: 0.375,
                px: 0.5,
                py: 0.125,
                borderRadius: 999,
                verticalAlign: 'middle',
                bgcolor: 'action.hover',
                border: '1px solid transparent',
                boxShadow: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                transition: 'background-color 150ms ease, box-shadow 150ms ease, transform 150ms ease, border-color 150ms ease',
                '&:hover': {
                    bgcolor: 'action.selected',
                    borderColor: 'divider',
                    boxShadow: 1,
                    transform: 'translateY(-1px)',
                },
                '&:focus-visible': {
                    outline: 'none',
                    borderColor: 'primary.main',
                    boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.2)',
                },
                '&, & *': {
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                },
                '&:hover .pm-agentic-section__citation-avatar': {
                    boxShadow: 1,
                },
                '&:hover .pm-agentic-section__citation-count': {
                    color: 'text.primary',
                },
            }}
        >
            <Box component="span" sx={{display: 'inline-flex', alignItems: 'center'}}>
                {(authors.length > 0 ? authors : [null]).slice(0, 3).map((author, index) => (
                    <Avatar
                        key={author ? buildAuthorIdentity(author) : 'unknown-author'}
                        component="span"
                        src={author?.headshot || undefined}
                        alt={author ? buildAuthorLabel(author) : 'Unknown author'}
                        sx={{
                            width: 22,
                            height: 22,
                            fontSize: 11,
                            border: '2px solid white',
                            ml: index === 0 ? 0 : -0.75,
                            position: 'relative',
                            zIndex: authors.length - index,
                            transition: 'box-shadow 150ms ease, transform 150ms ease',
                        }}
                        className="pm-agentic-section__citation-avatar"
                    >
                        {author ? buildAuthorInitials(author) : '?'}
                    </Avatar>
                ))}
            </Box>
            <Box
                component="span"
                className="pm-agentic-section__citation-count"
                sx={{
                    minWidth: 18,
                    height: 18,
                    px: 0.5,
                    color: 'text.secondary',
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: '18px',
                    textAlign: 'center',
                    transition: 'color 150ms ease',
                }}
            >
                {citationIds.length} Citation{citationIds.length > 1 ? 's' : ''}
            </Box>
        </ButtonBase>
    );
});

function replaceCitationMarkersInNode(node, citationsById, onCitationGroupClick, keyPrefix = 'node') {
    if (typeof node === 'string') {
        const matches = Array.from(node.matchAll(new RegExp(AGENTIC_SECTION_CITATION_MARKER_REGEX)));
        if (matches.length === 0) return node;

        const result = [];
        let lastIndex = 0;

        matches.forEach((match, index) => {
            const fullMatch = match[0] || '';
            const idsText = match[1] || '';
            const matchIndex = match.index || 0;

            if (matchIndex > lastIndex) {
                result.push(node.slice(lastIndex, matchIndex));
            }

            const citationIds = idsText
                .split(',')
                .map((entry) => entry.trim())
                .filter(Boolean);
            const citations = citationIds
                .map((citationId) => citationsById.get(citationId))
                .filter(Boolean);

            if (citations.length === 0) {
                result.push(fullMatch);
            } else {
                result.push(
                    <InlineCitationAvatarGroup
                        key={`${keyPrefix}-cite-${index}`}
                        citationIds={citationIds}
                        citations={citations}
                        onClick={() => onCitationGroupClick?.({citationIds, citations})}
                    />
                );
            }

            lastIndex = matchIndex + fullMatch.length;
        });

        if (lastIndex < node.length) {
            result.push(node.slice(lastIndex));
        }

        return result;
    }

    if (Array.isArray(node)) {
        return node.flatMap((child, index) => replaceCitationMarkersInNode(child, citationsById, onCitationGroupClick, `${keyPrefix}-${index}`));
    }

    if (React.isValidElement(node) && node.props?.children) {
        return React.cloneElement(node, {
            ...node.props,
            children: replaceCitationMarkersInNode(node.props.children, citationsById, onCitationGroupClick, keyPrefix),
        });
    }

    return node;
}

function AgenticSectionMarkdown({markdown, citations = [], onCitationGroupClick}) {
    if (!markdown || !markdown.trim()) return null;

    const citationsById = new Map((Array.isArray(citations) ? citations : []).map((citation) => [citation.citationId, citation]));

    const withInlineCitations = ({children, keyPrefix}) => replaceCitationMarkersInNode(
        React.Children.toArray(children),
        citationsById,
        onCitationGroupClick,
        keyPrefix,
    );

    return (
        <Box className="pm-agentic-section__response-markdown">
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    h1: ({node, children, ...props}) => (
                        <Typography variant="h1" fontWeight={600} sx={{mt: 2, mb: 1}} {...props}>
                            {withInlineCitations({children, keyPrefix: 'h1'})}
                        </Typography>
                    ),
                    h2: ({node, children, ...props}) => (
                        <Typography variant="h2" fontWeight={600} sx={{mt: 1.75, mb: 1}} {...props}>
                            {withInlineCitations({children, keyPrefix: 'h2'})}
                        </Typography>
                    ),
                    h3: ({node, children, ...props}) => (
                        <Typography variant="h3" fontWeight={600} sx={{mt: 1.5, mb: 0.75}} {...props}>
                            {withInlineCitations({children, keyPrefix: 'h3'})}
                        </Typography>
                    ),
                    h4: ({node, children, ...props}) => (
                        <Typography variant="h4" fontWeight={600} sx={{mt: 1.5, mb: 0.75}} {...props}>
                            {withInlineCitations({children, keyPrefix: 'h4'})}
                        </Typography>
                    ),
                    h5: ({node, children, ...props}) => (
                        <Typography variant="h5" fontWeight={600} sx={{mt: 1.25, mb: 0.5}} {...props}>
                            {withInlineCitations({children, keyPrefix: 'h5'})}
                        </Typography>
                    ),
                    h6: ({node, children, ...props}) => (
                        <Typography variant="h6" fontWeight={600} sx={{mt: 1.25, mb: 0.5}} {...props}>
                            {withInlineCitations({children, keyPrefix: 'h6'})}
                        </Typography>
                    ),
                    p: ({node, children, ...props}) => (
                        <Typography variant="body1" sx={{mb: 1.25, lineHeight: 1.7}} {...props}>
                            {withInlineCitations({children, keyPrefix: 'p'})}
                        </Typography>
                    ),
                    ul: ({node, ...props}) => (
                        <Box component="ul" sx={{pl: 3, mb: 1.25}} {...props} />
                    ),
                    ol: ({node, ...props}) => (
                        <Box component="ol" sx={{pl: 3, mb: 1.25}} {...props} />
                    ),
                    li: ({node, children, ...props}) => (
                        <Typography component="li" variant="body1" sx={{mb: 0.5}} {...props}>
                            {withInlineCitations({children, keyPrefix: 'li'})}
                        </Typography>
                    ),
                    blockquote: ({node, ...props}) => (
                        <Box
                            component="blockquote"
                            sx={{
                                borderLeft: '4px solid',
                                borderColor: 'primary.main',
                                pl: 2,
                                py: 0.5,
                                my: 1.5,
                                fontStyle: 'italic',
                                color: 'text.secondary',
                            }}
                            {...props}
                        />
                    ),
                    a: ({node, href, children, ...props}) => (
                        <Link href={href} target="_blank" rel="noopener noreferrer" {...props}>
                            {withInlineCitations({children, keyPrefix: 'a'})}
                        </Link>
                    ),
                    table: ({node, ...props}) => (
                        <Box className="pm-agentic-section__table-wrap">
                            <table {...props} />
                        </Box>
                    ),
                }}
            >
                {markdown}
            </ReactMarkdown>
        </Box>
    );
}

export default React.memo(AgenticSectionMarkdown);

